import { useEffect } from 'react';

import { useSocket } from '../../api/socket/SocketProvider';

const POLL_INTERVAL = 60000; // 60s fallback poll (in case socket misses something)

/**
 * Keeps a header count fresh: runs `refresh` on mount, on a 60s poll, when the tab regains focus and
 * on each of the given socket events. The events are nudges only — `refresh` re-asks the server,
 * which is the one place the count is scoped.
 */
export default function useLiveRefresh(refresh: () => void, events: string[]) {
    const { socket } = useSocket();

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, POLL_INTERVAL);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [refresh]);

    // The joined key keeps the effect stable when a caller passes a fresh array literal each render.
    const eventsKey = events.join('|');
    useEffect(() => {
        if (!socket) return;
        const names = eventsKey.split('|');
        names.forEach((event) => socket.on(event, refresh));
        return () => names.forEach((event) => socket.off(event, refresh));
    }, [socket, eventsKey, refresh]);
}
